import clsx from 'clsx'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import isologoSII from '../../../../../../../assets/isologo_SII.png'
import { Text } from '../../../../../ui'
import { formatCurrency, formatDate, formatRut } from '../../../../../../utils/formatter'
import { getConciliationDisplayStatus } from '../../../../../../utils/conciliationStatus'

const getRowId = row =>
  row?.idPrincipal ??
  row?.id_principal ??
  row?.egreso?.idPrincipal ??
  row?.egreso?.id_principal ??
  row?.id

const normalizeStatus = status =>
  String(status || '')
    .replace(/\s*undefined\s*/gi, '')
    .trim()
    .toUpperCase()

const statusBadgeClass = status => {
  const normalized = normalizeStatus(status)
  const isPartial = normalized.includes('PARCIAL')
  const isPaid = normalized.includes('PAGADO') || normalized.includes('COBRADO')
  const isPending =
    normalized.includes('POR PAGAR') ||
    normalized.includes('POR COBRAR') ||
    normalized.includes('PENDIENTE')

  return clsx(
    'inline-flex items-center justify-center px-3 py-1 rounded-full text-[0.6rem] sm:text-[0.65rem] font-semibold uppercase tracking-wider',
    {
      'text-amber-700 bg-amber-100': isPartial,
      'text-success bg-success/10': isPaid && !isPartial,
      'text-danger bg-danger/10': isPending && !isPartial,
      'text-muted bg-border/40': !isPartial && !isPaid && !isPending
    }
  )
}

const truncateText = (text, max = 15) => {
  if (!text) return ''
  return text.length > max ? `${text.slice(0, max)}...` : text
}

const TablePagoAnticipo = ({ rows = [], selectedIds = [], onSelectionChange, modalStatus }) => {
  const isSelected = id =>
    selectedIds.some(selectedId => String(selectedId) === String(id))

  const toggleRow = row => {
    const id = getRowId(row)
    if (id == null) return

    const nextIds = isSelected(id)
      ? selectedIds.filter(selectedId => String(selectedId) !== String(id))
      : [...selectedIds, id]

    onSelectionChange(
      nextIds,
      rows.filter(item => isSelectedIn(nextIds, getRowId(item)))
    )
  }

  const styles = {
    wrapper: 'overflow-x-auto rounded-3xl shadow-sm',
    table: 'w-full table-auto text-[0.725rem] border-collapse leading-tight',
    thead: 'sticky top-0 z-10 bg-gray-50',
    th: 'py-2 px-1 w-auto max-w-[120px] text-center break-words border-b border-gray-200 uppercase text-[0.6rem] font-bold text-gray-500',
    tr: 'border-b border-gray-100 hover:bg-gray-50 transition-colors',
    td: 'py-2 px-2 w-auto max-w-[200px] text-center break-words align-middle',
    smallCol: 'w-auto max-w-[80px]',
    wideCol: 'w-auto max-w-[300px]',
    mediumCol: 'w-auto max-w-[120px]',
    boldText: 'font-bold text-[0.65rem]',
    text: 'text-[0.705rem] text-gray-400 font-medium'
  }

  return (
    <div className={styles.wrapper}>
      <div className='mt-2'>
        <table className={styles.table}>
          <thead className={styles.thead}>
            <tr>
              {['', 'Fecha', 'Razón Social', 'Cuenta Corriente', 'Descripción', 'Cargo', 'Status', 'Acciones'].map(
                column => (
                  <th className={styles.th} key={column || 'selection'}>
                    {column}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody>
            {rows.length > 0 ? (
              rows.map(row => {
                const rowId = getRowId(row)
                const checked = isSelected(rowId)
                const cleanStatus = normalizeStatus(row.status)
                const displayStatus = getConciliationDisplayStatus(cleanStatus, modalStatus, checked)

                return (
                  <tr className={styles.tr} key={rowId}>
                    <td className={clsx(styles.td, styles.smallCol)}>
                      <div className='flex items-center justify-center gap-1'>
                        <input
                          type='checkbox'
                          className='rounded'
                          checked={checked}
                          onChange={() => toggleRow(row)}
                        />
                        <img src={isologoSII} alt='SII' className='h-6 w-auto rounded' />
                      </div>
                    </td>
                    <td className={clsx(styles.td, styles.smallCol)}>
                      <Text className={styles.boldText}>{formatDate(row.fecha)}</Text>
                    </td>
                    <td className={clsx(styles.td, styles.wideCol)}>
                      <Text
                        className={clsx(styles.boldText, 'truncate')}
                        title={row.razonSocial}
                      >
                        {truncateText(row.razonSocial)}
                      </Text>
                    </td>
                    <td className={clsx(styles.td, styles.wideCol)}>
                      <Text
                        className={clsx(styles.boldText, 'truncate')}
                        title={row.nombreCuenta}
                      >
                        {truncateText(row.nombreCuenta)}
                      </Text>
                      <Text className={styles.text}>{row.rut ? formatRut(row.rut) : ''}</Text>
                    </td>
                    <td className={clsx(styles.td, styles.wideCol)}>
                      <Text className={styles.boldText}>{row.nombreFolio}</Text>
                      <Text className={styles.text}>
                        {row.folio ?? row.numeroFolio ?? row.numeroDocumento}
                      </Text>
                    </td>
                    <td className={clsx(styles.td, styles.mediumCol)}>
                      <Text className={styles.boldText}>
                        {formatCurrency(row.montoTotal ?? row.montoCargo ?? row.monto ?? 0)}
                      </Text>
                    </td>
                    <td className={clsx(styles.td, styles.mediumCol)}>
                      <span className={statusBadgeClass(displayStatus)}>
                        {displayStatus || '—'}
                      </span>
                    </td>
                    <td className={clsx(styles.td, styles.mediumCol)}>
                      <div className='flex h-full items-center justify-center space-x-3'>
                        <div className='flex flex-col text-xs text-gray-500 items-center justify-center'>
                          <button
                            type='button'
                            className={clsx(
                              'flex items-center justify-center w-7 h-7 rounded-full shadow-full-xs transition-colors duration-150',
                              checked
                                ? 'bg-accent2 text-white hover:bg-accent2/90'
                                : 'bg-gray-100 text-gray-300 hover:bg-gray-200'
                            )}
                            onClick={() => toggleRow(row)}
                          >
                            <DoneAllIcon className='w-4 h-4' />
                          </button>
                          <Text className='mt-2'>
                            {checked ? 'Seleccionado' : 'Seleccionar'}
                          </Text>
                        </div>
                      </div>
                    </td>
                  </tr>
                )
              })
            ) : (
              <tr>
                <td colSpan={8} className='text-center py-4'>
                  No hay datos disponibles
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

const isSelectedIn = (selectedIds, id) =>
  selectedIds.some(selectedId => String(selectedId) === String(id))

export default TablePagoAnticipo
